import { useEffect, useRef } from 'react';

export type UsePollingProps = {
  callback: () => Promise<void> | void;
  interval: number;
  startImmediately?: boolean;
};

export function usePolling({
  callback,
  interval,
  startImmediately = true
}: UsePollingProps): { startPolling: () => void; stopPolling: () => void } {
  const savedCallback = useRef<() => Promise<void> | void>();
  const intervalId = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const poll = async () => {
    if (savedCallback.current) {
      await savedCallback.current(); // you're dispatching inside this
    }
  };

  const startPolling = () => {
    if (!intervalId.current) {
      poll(); // run once immediately
      intervalId.current = setInterval(poll, interval);
    }
  };

  const stopPolling = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
  };

  useEffect(() => {
    if (startImmediately) {
      startPolling();
    }
    return stopPolling; // cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval]);

  return { startPolling, stopPolling };
}

export default usePolling;
